// Page de confirmation d'email après inscription
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function ConfirmEmailPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('ecosystem_user') || '{}');
  const token = localStorage.getItem('ecosystem_token');

  const handleVerify = async () => {
    if (code.length < 6) {
      toast.error('Entre le code complet reçu par email');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/auth/verify-email?code=${code}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setVerified(true);
        // Update local user
        const updatedUser = { ...user, email_verified: true };
        localStorage.setItem('ecosystem_user', JSON.stringify(updatedUser));
        toast.success('Email confirmé !');
      } else {
        toast.error(data.detail || 'Code invalide');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/reseau/verification');
  };

  const handleContinue = () => {
    navigate('/reseau/verification');
  };

  if (verified) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-600" size={40} />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Email confirmé !
            </h1>
            
            <p className="text-gray-600 mb-6">
              Ton adresse email est maintenant vérifiée. 
              Continue vers la vérification d'identité pour débloquer toutes les fonctionnalités.
            </p>
            
            <Button
              onClick={handleContinue}
              className="w-full h-12 bg-green-600 hover:bg-green-700"
              data-testid="continue-verification-btn"
            >
              Vérifier mon identité
              <ArrowRight size={18} className="ml-2" />
            </Button>
            
            <button
              onClick={handleSkip}
              className="mt-4 text-gray-500 hover:text-gray-700 text-sm"
            >
              Plus tard
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="text-blue-600" size={36} />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Confirme ton email
          </h1>
          
          <p className="text-gray-600">
            Un code de confirmation a été envoyé à
          </p>
          <p className="font-medium text-gray-900">
            {user.email || 'ton adresse email'}
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
              Entre le code à 8 caractères
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXXXXXX"
              className="text-center text-2xl tracking-widest font-mono h-14"
              maxLength={8}
              data-testid="confirm-code-input"
            />
          </div>
          
          <Button
            onClick={handleVerify}
            disabled={loading || code.length < 6}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700"
            data-testid="verify-email-btn"
          >
            {loading ? (
              <RefreshCw className="animate-spin" size={20} />
            ) : (
              <>
                Vérifier
                <CheckCircle size={18} className="ml-2" />
              </>
            )}
          </Button>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 mb-2">
              Tu n'as pas reçu l'email ?
            </p>
            <p className="text-xs text-gray-400">
              Vérifie tes spams ou{' '}
              <button className="text-blue-600 hover:underline">
                renvoyer le code
              </button>
            </p>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-100">
            <button
              onClick={handleSkip}
              className="w-full text-center text-gray-500 hover:text-gray-700 text-sm"
            >
              Vérifier mon identité maintenant
            </button>
            <p className="text-xs text-gray-400 mt-2 text-center">
              La vérification est obligatoire pour publier
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
